import React, { forwardRef, useEffect, useState } from 'react';
import { Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { GIQChoice } from 'views/App/Portal/Policy/_types/giq';
import { Add, Close } from '@mui/icons-material';
import { isOtherValue } from './helpers';

type AnswersInputProps = {
  id: string;
  value: { value: string }[] | undefined;
  onChange: (choices: GIQChoice[]) => void;
};

const AnswersInput = forwardRef(({ id, value, onChange }: AnswersInputProps, ref) => {
  const getInitialItems = (value: { value: string }[] | undefined) => {
    const initialItems = [...(value || [])].filter((x) => !isOtherValue(x.value));
    if (!initialItems || initialItems.length === 0) return [{ value: '' }, { value: '' }];
    if (initialItems.length === 1) return [...initialItems, { value: '' }];
    return initialItems;
  };
  const [items, setItems] = useState<GIQChoice[]>(getInitialItems(value));
  const [allowOther, setAllowOther] = useState<boolean>(value?.find((x) => isOtherValue(x.value)) !== undefined);

  const handleInputChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = { value: value.trim() } as GIQChoice;
    setItems(newItems);
  };

  const handleAddItem = () => {
    setItems([...items, { value: '' } as GIQChoice]);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    if (newItems.length === 1 && !allowOther) newItems.push({ value: '' });
    setItems(newItems);
  };

  useEffect(() => {
    onChange([...items, ...(allowOther ? [{ value: 'OTHER' }] : [])]);
  }, [items, onChange, allowOther]);

  return (
    <Box
      id={id}
      sx={{
        display: 'grid',
        gap: 3,
      }}
    >
      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            label={false}
            fullWidth
            value={item.value}
            onChange={(e) => handleInputChange(index, e.target.value)}
            sx={{ pt: 0, '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
          />
          <Button
            className='icon-only'
            variant='text'
            color='neutral'
            onClick={() => handleRemoveItem(index)}
          >
            <Close />
          </Button>
        </Box>
      ))}
      <Stack direction='row'>
        <Button
          variant='text'
          color='neutral'
          onClick={handleAddItem}
          startIcon={<Add />}
          size='small'
        >
          {t('draftManuals.giq.properties.addOption')}
        </Button>
      </Stack>
      <Stack direction='row'>
        <FormControlLabel
          control={
            <Switch
              id={`other_${id}`}
              checked={allowOther ?? false}
              onChange={({ target }) => setAllowOther(target.checked)}
            />
          }
          label={
            <Stack>
              <Typography sx={{ fontWeight: 600 }}>{t('draftManuals.giq.properties.allowOther')}</Typography>
            </Stack>
          }
        />
      </Stack>
    </Box>
  );
});

export default AnswersInput;
